import { produce } from 'immer';
import { StateCreator } from 'zustand';

import { IProjectsStore } from './projects.store';

export interface FontSource {
  id: number;
  source: string;
  style: 'normal' | 'italic' | 'oblique';
  type?: 'ttf';
  variant?: string;
  weight: string;
  width: string;
}
export interface LibraryFont {
  id: number;
  family: string;
  sources: FontSource[];
}

/**
 * This is the state of the Fonts slice.
 */
export interface FontsState {
  library: Record<number, LibraryFont>;
  googleFonts: string[]; // Array of loaded Google font family names
}

/**
 * Actions related to the Fonts state.
 */
export interface FontsActions {
  setLibraryFont: (font: LibraryFont) => void;
  setLibraryFonts: (fonts: LibraryFont[]) => void;
  addGoogleFont: (fontFamily: string) => void;
  addGoogleFonts: (fontFamilies: string[]) => void;
  removeGoogleFont: (fontFamily: string) => void;
}

export type FontsSlice = {
  fonts: FontsState & FontsActions;
};

/**
 * Creates the Fonts slice. This function can be merged into
 * a larger store (e.g., a projects store).
 */
export const createFontsSlice: StateCreator<IProjectsStore, [], [], FontsSlice> = (set) => ({
  fonts: {
    library: {},

    googleFonts: ['Lato', 'Nunito', 'Source Code Pro'], // System Fonts

    setLibraryFonts: (fonts: LibraryFont[]) =>
      set(
        produce((draft: IProjectsStore) => {
          fonts.forEach((font) => {
            draft.fonts.library[font.id] = font;
          });
        })
      ),

    setLibraryFont: (font: LibraryFont) =>
      set(
        produce((draft: IProjectsStore) => {
          draft.fonts.library[font.id] = font;
        })
      ),

    addGoogleFonts: (fontFamilies: string[]) =>
      set(
        produce((draft: IProjectsStore) => {
          fontFamilies.forEach((fontFamily) => {
            if (!draft.fonts.googleFonts.includes(fontFamily)) {
              draft.fonts.googleFonts.push(fontFamily);
            }
          });
        })
      ),

    addGoogleFont: (fontFamily: string) =>
      set(
        produce((draft: IProjectsStore) => {
          if (!draft.fonts.googleFonts.includes(fontFamily)) {
            draft.fonts.googleFonts.push(fontFamily);
          }
        })
      ),

    removeGoogleFont: (fontFamily: string) =>
      set(
        produce((draft: IProjectsStore) => {
          const index = draft.fonts.googleFonts.indexOf(fontFamily);

          if (index > -1) {
            draft.fonts.googleFonts.splice(index, 1);
          }
        })
      ),
  },
});
